import { Windows95Window } from "../Windows95Window";
import { IUserData } from "../../Logic/HospitalNetCore";
import { DoctorRanks } from "../../Constants/DoctorRanks";
import { BaseTexture, Container, Graphics, ImageResource, NineSlicePlane, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { GAME_FONT, GAME_FONT_BOLD, GAME_FONT_ITALIC } from "../../Constants/Constants";
import { ScrollBar } from "../ScrollBar";
import { DEFAULT_TEXTURE_B64 } from "../../../engine/Constants/Constants";
import { HelperFunctions } from "../../../tsthree";
import { AchievementID, Achievements } from "../../Data/Achievements";
import { IWindowState } from "../../Types/IWindowState";
import { buttonify } from "../../../engine/HelperFunctions/buttonify";
import { PlayerDataSingleton } from "../../Logic/PlayerDataSingleton";
import { CharacterPortraitGenerator } from "../../Logic/CharacterPortraitGenerator";

const DOC_STATS_HEIGHT = 455;

export class Win95DoctorStats extends Windows95Window {

    private nameAndTitleText: Text;
    private xpText: Text;
    private nextLevelInText: Text;
    private xpProgressBarMask: Graphics;
    private xpProgressBarWidth: number;
    private correctDiagnosisPercentageText: Text;
    private achievementProgressText: Text;
    private profilePicSprite: Sprite;
    private achievementDescText: Text;
    private selectedAchievementBg: Graphics;

    private _onAchievementClick: (i: number) => void;

    private _achievementsPool: Array<{
        sprite: Sprite;
        name: Text;
        container: Container;
    }> = [];

    constructor(
        _settings: {
            onMinimise: () => void;
            onClose: () => void;
            windowStateRef: IWindowState<Win95DoctorStats>;
        }
    ) {
        super({
            title: "DoctorStats",
            iconKey: "doctorstats-icon",

            // skipFullscreenButton: true,
            windowStateRef: _settings.windowStateRef,
            dimensions: {
                width: 340,
                height: DOC_STATS_HEIGHT
            },
            onMinimise: () => _settings.onMinimise(),
            onClose: () => _settings.onClose(),
        });

        const sharedTextStyle = new TextStyle({
            fontFamily: GAME_FONT,
            fill: 0x000000,
            fontSize: 14,
            align: "left",
        });

        const logoBg = new NineSlicePlane(
            ENGINE.getPIXIResource("inner-container-nineslice") as Texture,
            16, 16, 16, 16
        );
        logoBg.position.set(16, 16);
        logoBg.width =
            logoBg.height =
                100;
        this.contentContainer.addChild(logoBg);

        const logoMask = new Graphics();
        logoMask.beginFill(0xff0000);
        logoMask.drawRect(2, 2, logoBg.width - 4, logoBg.height - 4)
        logoBg.addChild(logoMask);

        const picSprite = this.profilePicSprite = new Sprite(
            new Texture(new BaseTexture(DEFAULT_TEXTURE_B64))
        );
        HelperFunctions.smartScale2D(
            {x: 96, y: 96},
            picSprite
        );
        picSprite.anchor.set(0.5, 0.5);
        picSprite.position.set(
            Math.floor(logoBg.width * 0.5),
            Math.floor(logoBg.height * 0.5),
        );
        logoBg.addChild(picSprite);
        picSprite.mask = logoMask;

        const nameAndTitleRightText = this.nameAndTitleText = new Text("", sharedTextStyle);
        nameAndTitleRightText.position.set(16 + logoBg.width + 6, 32);
        nameAndTitleRightText.anchor.set(0, 0);
        this.contentContainer.addChild(nameAndTitleRightText);

        const progressBarBg = new NineSlicePlane(
            ENGINE.getPIXIResource("taskbar-timeiconbg-nineslice") as Texture
        );
        progressBarBg.position.set(
            16 + logoBg.width + 4, 70
        );
        this.contentContainer.addChild(progressBarBg);

        const progressBarContainer = new Container();
        progressBarContainer.position.set(0, 1);
        const progressBar = new Sprite(
            ENGINE.getPIXIResource("loading-progress") as Texture
        );
        progressBarBg.width = this.xpProgressBarWidth = progressBar.width + 1;
        progressBarBg.addChild(progressBarContainer);
        progressBarContainer.addChild(progressBar);
        const progressBarMask = this.xpProgressBarMask = new Graphics();
        progressBarMask.beginFill(0xff0000);
        progressBarMask.drawRect(0, 0, progressBarBg.width, progressBarBg.height);
        progressBarMask.endFill();
        progressBarContainer.mask = progressBarMask;
        progressBarContainer.addChild(progressBarMask);

        const xpAmount = this.xpText = new Text("0 XP",
            Object.assign({}, sharedTextStyle, {fontSize: 11})
        );
        xpAmount.position.set(
            16 + logoBg.width + 6, 90
        );
        this.contentContainer.addChild(xpAmount);

        const achievementProgressText = this.achievementProgressText = new Text("Achievement progress: ...%",
            Object.assign({}, sharedTextStyle, {align: "right", fontSize: 11})
        );
        achievementProgressText.anchor.set(1, 0);
        achievementProgressText.position.set(340 - 16, 114);
        this.contentContainer.addChild(achievementProgressText);

        const correctDiagnosisPercentage = this.correctDiagnosisPercentageText = new Text("Diagnosis Accuracy: ...%",
            Object.assign({}, sharedTextStyle, {align: "right", fontSize: 11})
        );
        correctDiagnosisPercentage.anchor.set(1, 0);
        correctDiagnosisPercentage.position.set(
            340 - 16, 114 + 230
        );
        this.contentContainer.addChild(correctDiagnosisPercentage);

        const nextLevelInText = this.nextLevelInText = new Text("Next level in ... XP",
            Object.assign({}, sharedTextStyle, {fontSize: 11})
        );
        nextLevelInText.anchor.set(1, 1);
        nextLevelInText.position.set(336, DOC_STATS_HEIGHT - 39);
        this.contentContainer.addChild(nextLevelInText);

        const achievementsContainer = new Container();
        achievementsContainer.position.set( 15, 130 );
        this.contentContainer.addChild(achievementsContainer);
        const achievementsContainerBg = new NineSlicePlane(
            ENGINE.getPIXIResource("inner-container-nineslice") as Texture,
            16, 16, 16, 16
        );
        achievementsContainerBg.width = 340 - 30;
        achievementsContainerBg.height = 230;
        achievementsContainer.addChild(achievementsContainerBg);
        buttonify(achievementsContainerBg, {
            disableButtonMode: true,
            onFire: () => {
                if(this._onAchievementClick)
                    this._onAchievementClick(-1);
            },
        });

        const achievementDescText = this.achievementDescText = new Text("", new TextStyle({
            fontFamily: GAME_FONT_ITALIC,
            fontSize: 11,
            align: "center",
            wordWrap: true,
            wordWrapWidth: achievementsContainerBg.width,
        }));
        achievementDescText.anchor.set(0.5, 0);
        achievementDescText.position.set(
            2 + (achievementsContainerBg.width / 2),
            achievementsContainerBg.height + 2
        );
        achievementsContainer.addChild(achievementDescText);

        const mask = new Graphics();
        mask.beginFill(0xff0000);
        mask.drawRect(2, 2, achievementsContainerBg.width - 4, achievementsContainerBg.height - 4);
        mask.endFill();


        const achievementIconsContainer = new Container();
        const selectedAchievementBg = this.selectedAchievementBg = new Graphics();
        selectedAchievementBg.beginFill(0x0000ff, 1);
        selectedAchievementBg.drawRect(0, 0, 32, 32);
        selectedAchievementBg.endFill();
        selectedAchievementBg.visible = false;
        achievementIconsContainer.addChild(selectedAchievementBg);

        achievementIconsContainer.mask = mask;
        achievementIconsContainer.addChild(mask);
        achievementsContainer.addChild(achievementIconsContainer);
        for(let i = 0; i < 20; i++) {
            const container = new Container();
            container.position.set(
                30 + (70 * (i % 4)),
                10 + (70 * Math.floor(i / 4))
            );

            const sprite = new Sprite(
                // new Texture(new BaseTexture(DEFAULT_TEXTURE_B64))
            );
            sprite.width = sprite.height = 32;
            container.addChild(sprite);
            const text = new Text("", new TextStyle({
                fontFamily: GAME_FONT,
                fill: 0x000000,
                fontSize: 11,
                wordWrap: true,
                wordWrapWidth: 64,
                align: "center",
            }));
            text.anchor.set(0.5, 0);
            text.position.set(16, 32 + 2);
            container.addChild(text);
            achievementIconsContainer.addChild(container);

            buttonify(container, {
                onFire: () => {
                    if(!this._onAchievementClick || container.visible === false || text.text === "") return;
                    this._onAchievementClick(i);
                    // this.achievementDescText.text =
                }
            });

            this._achievementsPool.push({
                sprite: sprite as Sprite,
                name: text as Text,
                container: container as Container
            });
        }

        const achievementsScrollBar = new ScrollBar({
            height: 223,
            onScroll: (_currIncrements: number) => {

            }
        });
        achievementsScrollBar.position.set(310 - 2, 2);
        achievementsContainer.addChild(achievementsScrollBar);
    }

    public async update(
        _userData?: IUserData
    ): Promise<void> {
        if(!_userData) {
            // this.windowName = "DoctorStats - OFFLINE";
            // return;
            _userData = {
                achievements: PlayerDataSingleton.getAchievements(),
                docScore: PlayerDataSingleton.getDocScore(),
                id: "",
                displayName: PlayerDataSingleton.getDisplayName(),
                numOfSolvedCases: PlayerDataSingleton.getNumOfSolvedCases(),
                numOfIncorrectDiagnoses: PlayerDataSingleton.getNumOfIncorrectDiagnoses(),
                characterCreatorSettings: PlayerDataSingleton.getCharacterCreatorSettings(),
            };
        }
        this.windowName = "DoctorStats";
        const docRankIndex = DoctorRanks.findIndex(
            (e) => e.threshold > _userData.docScore) - 1;
        const rankData = DoctorRanks[
            docRankIndex
        ];
        const nextRankData = DoctorRanks[docRankIndex+1];
        this.nameAndTitleText.text = `${_userData.displayName}\n${rankData.name}`;
        this.xpText.text = Math.floor(_userData.docScore).toString() + " XP";
        this.xpProgressBarMask.width = this.xpProgressBarWidth * (_userData.docScore / nextRankData.threshold);

        if(DoctorRanks[docRankIndex+1]) {
            this.nextLevelInText.text = `Next level in ${Math.ceil(nextRankData.threshold - _userData.docScore)} XP (${DoctorRanks[docRankIndex+1].name})`;
        } else {
            this.nextLevelInText.text = `Next level in ${Math.ceil(nextRankData.threshold - _userData.docScore)} XP`;
        }

        const diagnosisAccuracy =
            Math.round(
                _userData.numOfSolvedCases / (_userData.numOfIncorrectDiagnoses + _userData.numOfSolvedCases) * 1000
            ) / 10;
        this.correctDiagnosisPercentageText.text = `Diagnosis Accuracy: ${
            Number.isNaN(diagnosisAccuracy) ? 0 : diagnosisAccuracy 
        }%`;

        this.achievementProgressText.text = `Achievement progress: ${
            (PlayerDataSingleton.getAchievements().length)
        }/${(Object.keys(Achievements).length - 2)}`;

        this._onAchievementClick = (_index: number) => {
            this._achievementsPool.forEach((e) => e.name.style.fill = 0x000000);
            if(_index === -1) {
                this.achievementDescText.text = "";
                this.selectedAchievementBg.visible = false;
                return;
            } else {
                this._achievementsPool[_index].name.style.fill = 0xffffff;
                this.selectedAchievementBg.visible = true;
            }
            const id = (_index + 1) as AchievementID;
            if(PlayerDataSingleton.hasAchievement(id)) {
                this.achievementDescText.text = Achievements[id].description;
            } else {
                this.achievementDescText.text = "";
            }
            this.selectedAchievementBg.width = this._achievementsPool[_index].container.width + 8;
            this.selectedAchievementBg.height = this._achievementsPool[_index].container.height + 4;
            this.selectedAchievementBg.position.set(
                this._achievementsPool[_index].container.position.x - (this.selectedAchievementBg.width / 4) - 1,
                this._achievementsPool[_index].container.position.y,
            );
            this._achievementsPool.forEach((e) => e.name.style.fill = 0x000000);
            this._achievementsPool[_index].name.style.fill = 0xffffff;
        };

        let _i = 0;
        Object.keys(Achievements)
            .forEach((e, i) => {
                if(i === 0) _i--;
                else
                    _i++;
                if(
                    i === 0 ||
                    i >= Object.keys(Achievements).length ||
                    i >= AchievementID.NUM_OF_ACHIEVEMENTS
                ) return;

                this._achievementsPool[_i].container.visible = true;
                if(_userData.achievements.indexOf(i) === -1) {
                    this._achievementsPool[_i].container.alpha = 0.5;
                }

                this._achievementsPool[_i].name.text = Achievements[i as AchievementID].name;
                if(PlayerDataSingleton.hasAchievement(i as AchievementID)) {
                    this._achievementsPool[_i].sprite.texture = ENGINE.getTexture(
                        `achievement-${i}`
                    );
                } else {
                    this._achievementsPool[_i].sprite.texture = ENGINE.getTexture(
                        `help-icon-24px`
                    );
                }
            });

        try {
            this.profilePicSprite.texture?.destroy();
            const characterCreator = new CharacterPortraitGenerator();
            characterCreator.update(_userData.characterCreatorSettings);
            this.profilePicSprite.texture =
                characterCreator.createPortraitTexture();
            characterCreator.destroy({
                texture: false,
                baseTexture: false,
                children: true,
            });
            HelperFunctions.smartScale2D(
                {x: 128, y: 128},
                this.profilePicSprite
            );
        } catch (err) {
            console.warn(err);
        }
    }
}